import React, { useEffect, useState } from "react";
import { Loader2, Download, UserSquare2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const field = "h-10 px-3 bg-[#0a0a0a] border border-white/10 text-sm text-white font-mono focus:outline-none focus:border-[var(--org-accent)]";

// One page · one player · one game — same stat-line rules as Weekly Wins, rendered as the Spotlight slide.
export function SinglePlayerCard() {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [games, setGames] = useState(null);
  const [gameDate, setGameDate] = useState("");
  const [busy, setBusy] = useState("");
  const [card, setCard] = useState(null);

  useEffect(() => { api.weeklyWinsPlayers().then(setPlayers).catch(() => {}); }, []);

  const pickPlayer = async (id) => {
    setPlayerId(id); setGames(null); setGameDate(""); setCard(null);
    if (!id) return;
    setBusy("games");
    try { const r = await api.singleCardGames(id); setGames(r.games); if (r.games[0]) setGameDate(r.games[0].date); }
    catch (e) { toast.error("Could not load games", { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  const create = async () => {
    setBusy("create");
    try { const r = await api.singleCardCreate(playerId, gameDate); setCard(r); toast.success("Card ready"); }
    catch (e) { toast.error("Card failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-white/10 bg-[#141414] p-4" data-testid="single-card">
      <div className="flex items-center gap-2 mb-3">
        <UserSquare2 className="w-4 h-4 text-[var(--org-accent)]" />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--org-accent)]">Single Player Card</span>
        <span className="text-xs text-[#757575]">one page · one player · one game</span>
      </div>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <select value={playerId} onChange={e => pickPlayer(e.target.value)} className={`${field} w-full`} data-testid="single-card-player">
          <option value="">Pick a player…</option>
          {players.map(p => <option key={p.id} value={p.id}>{p.name} · {p.team}</option>)}
        </select>
        <select value={gameDate} onChange={e => setGameDate(e.target.value)} disabled={!games?.length} className={`${field} w-full disabled:opacity-40`} data-testid="single-card-game">
          {!games && <option value="">{busy === "games" ? "Loading games…" : "Pick a player first"}</option>}
          {games && games.length === 0 && <option value="">No games this season yet</option>}
          {games?.map(g => <option key={g.date} value={g.date}>{g.date} · {g.home ? "vs." : "at"} {g.opponent} · {g.pts} PTS {g.reb} REB {g.ast} AST</option>)}
        </select>
        <button onClick={create} disabled={!playerId || !gameDate || busy === "create"} className="inline-flex items-center justify-center gap-2 px-4 h-10 bg-[var(--org-accent)] text-black text-[10px] uppercase tracking-[0.25em] font-bold disabled:opacity-40" data-testid="single-card-create">
          {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy === "create" ? "Rendering…" : "Create card"}
        </button>
      </div>
      {card && (
        <div className="mt-4 grid sm:grid-cols-[240px_1fr] gap-4 items-start" data-testid="single-card-result">
          <a href={card.image_url} target="_blank" rel="noreferrer" className="block border border-white/10"><img src={card.image_url} alt="card" className="w-full" /></a>
          <div className="space-y-2 text-sm">
            <div className="font-bold text-white text-lg">{card.card.first} <span className="uppercase">{card.card.last}</span> <span className="text-xs text-[#a0a0a0] font-normal">{card.card.team} · {card.card.level}</span></div>
            <div className="text-[#d0d0d0]">{card.card.result} {card.card.context}{card.card.tag ? ` · ${card.card.tag}` : ""}</div>
            <div className="text-xs text-[#a0a0a0]">{card.card.box.pts} PTS{(card.card.secondary || []).map(s => ` · ${s}`).join("")}</div>
            <a href={card.image_url} download={card.filename} className="inline-flex items-center gap-2 px-3 h-10 border border-white/15 text-[10px] uppercase tracking-[0.25em] font-bold text-white hover:border-[var(--org-accent)]" data-testid="single-card-download"><Download className="w-4 h-4" />Download PNG</a>
            {card.card.missing_photo && <div className="text-xs text-amber-300">No profile photo — the card shows the silhouette.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
